import { CartItemProduct } from "@app/components/CartItems/CartItemProduct"
import { Cart } from "@app/types/cart"
import { CartItem } from "@app/types/cartItem"
import { Product } from "@app/types/product"
import React, { Dispatch, SetStateAction, useMemo, useState } from "react"

interface CartItemsProps {
  cart: Cart;
  products: Product[];
  isMiniCart?: boolean;
  zCodeDisclaimer?: string;
}

interface CartItemWithZCode {
  item: CartItem;
  product: Product;
  zCodeItem: CartItem;
  zCodeProduct: Product;
}

export const CartItems = ({ cart, products, isMiniCart, zCodeDisclaimer }: CartItemsProps) => {
  const [isLoading, setIsLoading] = useState(false)

  // group items by collection
  const groupedItems = cart.items.reduce(
    (acc, item) => {
      const product = products.find((p) => p.handle === item.handle)
      if (!product) return acc

      const collection = product.collections?.edges?.[0]?.node?.title || "Other"
      if (!acc[collection]) acc[collection] = []
      acc[collection].push(item)

      return acc
    },
    {} as { [key: string]: CartItem[] },
  )

  return (
    <div className={`flex flex-col gap-0 max-h-full ${isMiniCart ? "p-4 pb-6 overflow-y-auto" : ""}`}>
      {Object.entries(groupedItems).length > 1 ? (
        <GroupedItems
          groupedItems={groupedItems}
          products={products}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          zCodeDisclaimer={zCodeDisclaimer}
        />
      ) : (
        <SimpleItems items={cart.items} products={products} isLoading={isLoading} setIsLoading={setIsLoading} zCodeDisclaimer={zCodeDisclaimer} />
      )}
    </div>
  )
}

const GroupedItems = ({
  groupedItems,
  products,
  isLoading,
  isMiniCart,
  setIsLoading,
  zCodeDisclaimer,
}: {
  groupedItems: { [key: string]: CartItem[] };
  products: Product[];
  isLoading: boolean;
  isMiniCart?: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  zCodeDisclaimer?: string;
}) =>
  Object.entries(groupedItems).map(([collection, items]: any) => {
    return (
      <div key={collection}
           className={`mt-4 first:mt-0 ${isMiniCart ? "" : "border-b border-neutrals-lightGrey last:border-none"}`}>
        <div className={`font-medium text-brand-bistre ${isMiniCart ? "text-md" : "text-lg"}`}>{collection}</div>
        <SimpleItems items={items} products={products} isLoading={isLoading} setIsLoading={setIsLoading} zCodeDisclaimer={zCodeDisclaimer} />
      </div>
    )
  })

const SimpleItems = ({
  items,
  products,
  isLoading,
  setIsLoading,
  zCodeDisclaimer,
}: {
  items: Cart["items"];
  products: Product[];
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  zCodeDisclaimer?: string;
}) => {
  const itemsWithZCode: CartItemWithZCode[] = useMemo(() => {
    return items.map((item) => {
      let zCodeItem: CartItem | undefined
      let zCodeProduct: Product | undefined

      // Check for Z-code item
      if (item.properties._zVariantId) {
        zCodeItem = items?.find(chkItem => chkItem.variant_id.toString() === item.properties._zVariantId)
        zCodeProduct = products.find((p) => p.handle === zCodeItem?.handle)
        if (zCodeItem && !zCodeProduct) {
          return null
        }
      }

      const product = products.find((p) => p.handle === item?.handle)
      if (!product) {
        return null
      }

      if (item.properties._nonZVariantId) {
        // Current product in cart, don't show Z-code as a line item
        if (items?.find(chkItem => chkItem.variant_id.toString() === item.properties._nonZVariantId)) {
          return null
        }
      }

      return {
        item: item,
        product: product,
        zCodeItem: zCodeItem,
        zCodeProduct: zCodeProduct
      }
    }).filter(item => !!item) as CartItemWithZCode[]
  }, [items, products])

  return itemsWithZCode.map((item, index) => {
    return (
      <CartItemProduct
        key={index}
        cartItem={item.item}
        product={item.product}
        zCodeCartItem={item.zCodeItem}
        zCodeProduct={item.zCodeProduct}
        zCodeDisclaimer={zCodeDisclaimer}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    )
  })
}
